import React from 'react';
import { Inbox, Calendar, Sunrise } from 'lucide-react';
import { ExecInboxStage, EXEC_INBOX_UNSORTED_STAGE_ID } from '../../types';
import { StageIcon } from './stageIcons';
import { ExecutiveBriefingOption, profileLabel } from '../../utils/executiveBriefingAccess';

export type ExecView = 'briefing' | 'inbox' | 'calendar' | 'settings';

export interface MailboxOption {
  uid: string;
  name: string;
  email: string;
}

export interface StageCount {
  total: number;
  unread: number;
}

interface Props {
  mailboxes: MailboxOption[];
  selectedMailbox: string;
  onSelectMailbox: (uid: string) => void;
  executives: ExecutiveBriefingOption[];
  selectedExecutive: string;
  onSelectExecutive: (uid: string) => void;
  briefingOnly?: boolean;
  view: ExecView;
  onSelectView: (v: ExecView) => void;
  stages: ExecInboxStage[];
  counts: Record<string, StageCount>;
  selectedStage: string;
  onSelectStage: (stageId: string) => void;
}

const ExecInboxSidebar: React.FC<Props> = ({
  mailboxes, selectedMailbox, onSelectMailbox,
  executives, selectedExecutive, onSelectExecutive,
  briefingOnly = false,
  view, onSelectView, stages, counts, selectedStage, onSelectStage,
}) => {
  const renderStage = (id: string, label: string, accent: string, icon?: string) => {
    const c = counts[id] || { total: 0, unread: 0 };
    const active = view === 'inbox' && selectedStage === id;
    return (
      <button
        key={id}
        className={`exec-stage-item${active ? ' active' : ''}`}
        style={{ ['--stage-accent' as any]: accent }}
        onClick={() => { onSelectView('inbox'); onSelectStage(id); }}
      >
        <span className="exec-stage-dot" style={{ background: accent }} />
        <span style={{ display: 'flex', opacity: 0.7 }}><StageIcon name={icon} size={14} /></span>
        <span className="exec-stage-name">{label}</span>
        {c.unread > 0
          ? <span className="exec-stage-count unread">{c.unread}</span>
          : c.total > 0 ? <span className="exec-stage-count">{c.total}</span> : null}
      </button>
    );
  };

  return (
    <aside className="exec-sidebar">
      <div className="exec-mailbox-switch">
        <span className="exec-mailbox-switch-label">Briefing for</span>
        {executives.length > 1 ? (
          <select
            className="exec-mailbox-select"
            value={selectedExecutive}
            onChange={(e) => onSelectExecutive(e.target.value)}
          >
            {executives.map((e) => (
              <option key={e.uid} value={e.uid}>
                {e.name} ({profileLabel(e.profile)})
              </option>
            ))}
          </select>
        ) : (
          <div className="exec-mailbox-select" style={{ cursor: 'default' }}>
            {executives[0]?.name || 'Executive'}
            {executives[0] ? ` (${profileLabel(executives[0].profile)})` : ''}
          </div>
        )}
      </div>

      {!briefingOnly && (
        <div className="exec-mailbox-switch">
          <span className="exec-mailbox-switch-label">Mailbox</span>
          {mailboxes.length > 1 ? (
            <select
              className="exec-mailbox-select"
              value={selectedMailbox}
              onChange={(e) => onSelectMailbox(e.target.value)}
            >
              {mailboxes.map((m) => (
                <option key={m.uid} value={m.uid}>{m.name}</option>
              ))}
            </select>
          ) : (
            <div className="exec-mailbox-select" style={{ cursor: 'default' }}>
              {mailboxes[0]?.name || 'My mailbox'}
            </div>
          )}
        </div>
      )}

      <div className="exec-views">
        <button className={`exec-view-btn${view === 'briefing' ? ' active' : ''}`} onClick={() => onSelectView('briefing')}>
          <Sunrise size={14} /><span>Briefing</span>
        </button>
        {!briefingOnly && (
          <>
            <button className={`exec-view-btn${view === 'inbox' ? ' active' : ''}`} onClick={() => onSelectView('inbox')}>
              <Inbox size={14} /><span>Inbox</span>
            </button>
            <button className={`exec-view-btn${view === 'calendar' ? ' active' : ''}`} onClick={() => onSelectView('calendar')}>
              <Calendar size={14} /><span>Calendar</span>
            </button>
          </>
        )}
      </div>

      {view === 'inbox' && (
        <nav className="exec-funnel">
          <span className="exec-funnel-label">Funnel</span>
          {renderStage(EXEC_INBOX_UNSORTED_STAGE_ID, 'Unsorted', '#94A3B8', 'Inbox')}
          {stages
            .filter((s) => !s.archived)
            .sort((a, b) => a.order - b.order)
            .map((s) => renderStage(s.id, s.label, s.accent, s.icon))}
        </nav>
      )}
    </aside>
  );
};

export default ExecInboxSidebar;
